import React from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { H2, TextField } from '~/components';
import { AvatarField } from '~/components/ra';
import { TreeNode } from '~/types/Menu';
import Section from '~/layout/Section/Section';
import EditAction from './EditAction';
import DeleteAction from './DeleteAction';
import CreateAction from './CreateAction';
import { hasTranslation } from '~/utils';
import { queryClient } from '~/App';

const l = (key: string): string => `resources.menu.fields.${key}`;

interface Props {
  selectedNode: TreeNode | null;
  menuId: string;
  loadChildren?: (id: string, forceReload?: boolean) => void;
}

const DetailsItem = (props: Props) => {
  const { selectedNode, menuId, loadChildren } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const classes = useStyles();
  const { permissions } = usePermissions();
  if (!permissions?.includes('dynamicMenu.view') || !selectedNode) return null;
  return (
    <Section noPaddingRight>
      <Grid container justify='space-between' className={classes.bottomSpacing2}>
        <Grid container item alignItems='center' xs={4}>
          <AvatarField
            record={selectedNode}
            source='branding.icon'
            colorSource='branding.color'
            resource={`menu/${menuId}/items`}
            noText={true}
            size='50px'
            onSuccess={() => {
              if (selectedNode?.parentId && selectedNode?.parentId !== menuId) {
                loadChildren?.(selectedNode?.parentId!, true);
              } else {
                queryClient.invalidateQueries({
                  queryKey: ['menu', menuId, 'items', '$'],
                });
              }
            }}
            updatable={permissions?.includes('dynamicMenu.update')}
          />
          <H2 bottomSpacing={0} topSpacing={0} className={classes.header}>
            {t('itemDetails')}
          </H2>
        </Grid>
        <Grid item container xs={8} id='section-actions' justify='flex-end'>
          {permissions?.includes('dynamicMenu.create') && (
            <Grid item>
              {selectedNode?.itemType === 'GROUP' && (
                <CreateAction
                  selectedType='menuItem'
                  menuId={menuId}
                  parentId={selectedNode?.id}
                  loadChildren={loadChildren}
                />
              )}
            </Grid>
          )}
          {permissions?.includes('dynamicMenu.update') && (
            <Grid item>
              <EditAction
                selectedNode={selectedNode}
                selectedType='menuItem'
                menuId={menuId}
                parentId={selectedNode?.parentId}
                loadChildren={loadChildren}
              />
            </Grid>
          )}
          {permissions?.includes('dynamicMenu.delete') && (
            <Grid item>
              <DeleteAction
                selectedNode={selectedNode}
                selectedType='menuItem'
                menuId={menuId}
                parentId={selectedNode?.parentId}
                loadChildren={loadChildren}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Section noPaddings>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <TextField label={t('id')} ellipsis>
              {selectedNode?.id}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField ellipsis label={t('code')}>
              {selectedNode?.code}
            </TextField>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <TextField label={t('itemType')}>
              {selectedNode?.itemType && hasTranslation(l(selectedNode?.itemType as string))
                ? t(selectedNode?.itemType)
                : selectedNode?.itemType}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            {selectedNode?.itemType === 'SERVICE' && (
              <TextField
                label={t('serviceId')}
                showLink={
                  selectedNode?.serviceId
                    ? `/paymentServices/${selectedNode?.serviceId}/show`
                    : undefined
                }
              >
                {selectedNode?.serviceId}
              </TextField>
            )}
            {selectedNode?.itemType === 'ACTION' && (
              <TextField label={t('actionId')}>{selectedNode?.actionId}</TextField>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <TextField ellipsis label={t('priorityIndex')}>
              {selectedNode?.index}
            </TextField>
          </Grid>
          {selectedNode?.fallbackName && (
            <Grid item xs={6}>
              <TextField ellipsis label={t('fallbackName')}>
                {selectedNode?.fallbackName}
              </TextField>
            </Grid>
          )}
        </Grid>
        {selectedNode?.fallbackDescription && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField ellipsis multiline rows={5} label={t('fallbackDescription')}>
                {selectedNode?.fallbackDescription}
              </TextField>
            </Grid>
          </Grid>
        )}
      </Section>
    </Section>
  );
};

const useStyles = makeStyles((theme) => ({
  bottomSpacing2: {
    marginBottom: theme.spacing(2),
  },
  header: {
    marginLeft: theme.spacing(1),
  },
}));

export default DetailsItem;
